// Detects when a NEW frontend build has been deployed while the app is open,
// so we can invite the user to reload instead of leaving them on stale code
// until they happen to hard-refresh.
//
// How: the running page references a hashed main bundle (…/static/js/main.<hash>.js).
// Every deploy produces a new hash. We periodically fetch index.html (bypassing
// the cache) and compare the bundle it references against the one we booted with.
// If it changed, a new version is live. We NEVER force a reload — the UI shows a
// dismissible prompt, so no one loses unsaved work in a form mid-task.
import { useState, useEffect, useRef, useCallback } from 'react';

const POLL_MS = 5 * 60 * 1000; // re-check every 5 minutes (and on tab focus)
const BUNDLE_RE = /\/static\/js\/main\.[\w]+\.js/;

// The bundle the currently-open app actually booted with.
function runningBundleId() {
  const s = document.querySelector('script[src*="/static/js/main."]');
  const m = s && s.getAttribute('src')?.match(BUNDLE_RE);
  return m ? m[0] : null;
}

// The bundle the server is serving right now.
async function fetchBundleId() {
  try {
    const res = await fetch(`/?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(BUNDLE_RE);
    return m ? m[0] : String(html.length); // weak fallback if hashing is off (dev)
  } catch {
    return null; // offline / backend cold — try again next tick
  }
}

export function useAppUpdate() {
  const [updateReady, setUpdateReady] = useState(false);
  const baseline = useRef(runningBundleId());

  const check = useCallback(async () => {
    if (document.hidden) return;
    const id = await fetchBundleId();
    if (!id) return;
    if (baseline.current == null) { baseline.current = id; return; } // establish baseline
    if (id !== baseline.current) setUpdateReady(true);
  }, []);

  useEffect(() => {
    check(); // baseline / first check shortly after load
    const t = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [check]);

  return updateReady;
}

export default useAppUpdate;
